import moment from "moment-timezone";
import { StackedColumnsDateChartSeries } from "./stackedColumnsDateChart";
import { AxisRenderer, CategoryAxis, DateAxis, XYSeries } from "@amcharts/amcharts4/charts";
import { NumberFormatter } from "@amcharts/amcharts4/core";

export function formatTooltipDay(date: number | string) {
  const dateNumber = typeof date === 'string' ? parseInt(date) : date;
  return moment.utc(dateNumber).format('ll');
}

export function formatTooltipDayWithHourRange(date: number, timeZone?: string) {
  const start = moment.utc(date).tz(timeZone || 'UTC');
  const end = moment.utc(date).add(1, 'hour').tz(timeZone || 'UTC');
  return `${start.format('LLLL LT')} - ${end.format('LT')}`;
}

export function getSingleColumnDateChartTooltipAdapter<Data extends { date: number } | { date: string } >(params: {
  colour: string,
  countKey: keyof Data,
  countDescription: string
}) {
  return (_: any, target: any) => {
    if (!target.tooltipDataItem) {
      return;
    }
    const data = target.tooltipDataItem.dataContext as Data;
    const date = formatTooltipDay(data.date);
    const tooltip = `
    <div class="column-date-chart-tooltip-container" data-date="${data.date}" data-seriesName="${params.countKey as string}">
      <span class="column-date-chart__tooltip-title">${date}</span>
      </br>
      <span>
        <div class="chart-key" style="--colour: ${params.colour}"></div>
        &nbsp;${params.countDescription}: ${data[params.countKey]}
      </span>
    </div>`;
    return tooltip;
  };
}

export function getStackedColumnsDateChartTooltipAdapter<Data extends { date: string } | { date: number }>(params: {
  seriesName: string,
  totalDescription: string,
  formatData?: (dataForSeries: any, allData?: Data) => string,
  amchartsNumberFormat?: string,
  onlyShowCurrentSeries?: boolean,
  formatTooltipDate?: (date: number) => string,
  dateKey?: keyof Data
}) {
  return (_: any, target: any) => {
    if (!target.tooltipDataItem) {
      return;
    }
    const data = target.tooltipDataItem.dataContext as Data;
    const unFormattedDate = params.dateKey ? (data as any)[params.dateKey] : data.date;
    const date = params.formatTooltipDate ? params.formatTooltipDate(unFormattedDate) : unFormattedDate;
    const allSeries = target.clonedFrom.dummyData.allSeries as StackedColumnsDateChartSeries<Data>[];
    const visibleSeries = params.onlyShowCurrentSeries ? allSeries.filter(series => series.dataKey === params.seriesName) : allSeries.filter(series => series.visible);
    const seriesInformation = visibleSeries.map((seriesData) => `</br><span><div class="chart-key" style="--colour: ${seriesData.colour}"></div>&nbsp;${seriesData.description}: ${params.formatData ? params.formatData(data[seriesData.dataKey]) : `{${seriesData.dataKey as string}.formatNumber('${params.amchartsNumberFormat}')}`}</span>`).join('');

    let tooltip;
    if (params.totalDescription) {
      tooltip = `<div class="column-date-chart-tooltip-container" data-date="${data.date}" data-seriesName="${params.seriesName}"><span class="column-date-chart__tooltip-title">${date}</span>
      ${seriesInformation}
      ${visibleSeries.length === allSeries.length ? `</br><span>&nbsp;${params.totalDescription}: ${params.formatData ? params.formatData(undefined, data) : `{valueY.total.formatNumber('${params.amchartsNumberFormat}')}`}</span>` : ''}
      </div>`;
    } else {
      tooltip = `<div class="column-date-chart-tooltip-container" data-date="${data.date}" data-seriesName="${params.seriesName}"><span class="column-date-chart__tooltip-title">${date}</span>
      ${seriesInformation}
      </div>`;
    }
    return tooltip;
  };
}


export function getStackedColumnsDateChartAxisTooltipAdapter<Data extends { date: string } | { date: number }>(params: {
  totalDescription: string,
  formatData?: (dataForSeries: any, allData?: Data) => string,
  amchartsNumberFormat?: string,
  formatTooltipDate?: (date: number) => string,
  dateKey?: keyof Data,
  hideNullValues?: boolean
}) {
  return (_: string, target: CategoryAxis<AxisRenderer> | DateAxis<AxisRenderer>) => {
    let dataItem = target.getSeriesDataItem(
        target.chart.series.getIndex(0) as XYSeries,
        target.toAxisPosition((target.chart as any).cursor.xPosition),
        true
      ),
      data = dataItem.dataContext as Data;

    const unFormattedDate = params.dateKey ? (data as any)[params.dateKey] : data?.date;
    const date = params.formatTooltipDate ? params.formatTooltipDate(unFormattedDate) : unFormattedDate;

    let tooltipText = `[font-weight: 600]${date}[/]\n`;

    const allSeries = target.dummyData.allSeries as StackedColumnsDateChartSeries<Data>[];

    let seriesInformation = "";
    allSeries.forEach(function (series) {
      if (series.visible && !(params.hideNullValues && data[series.dataKey] === null)) {
        const formattedValue = params.formatData ?
          params.formatData(data[series.dataKey])
          :
          new NumberFormatter().format(data[series.dataKey] as string | number, params.amchartsNumberFormat);

        seriesInformation += "[" + series.colour + `font-size: 18px]\u25A0 [/]` + series.description + ": " + formattedValue + "\n";
      }
    });

    tooltipText += seriesInformation;

    if (params.totalDescription && allSeries.every(series => series.visible)) {
      const total = allSeries.reduce((acc, series) => acc + (data[series.dataKey] as number), 0);
      const formattedTotalValue = params.formatData ?
        params.formatData(undefined, data)
        :
        new NumberFormatter().format(total, params.amchartsNumberFormat);

      tooltipText += `[font-weight: 600]${params.totalDescription}[/]: ` + formattedTotalValue + `[font-size: 18px]\u00A0[/]`;
    }

    return tooltipText;
  };
}
